import React, { useEffect, useMemo, useState } from 'react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import Box from 'components/Box'
import Flex from 'components/Flex'
import Dropdown from 'components/Dropdown'
import { DropdownItem } from 'components/Dropdown/types'
import Icon from 'components/Icon'
import Skeleton from 'components/SkeletonLoader'
import Text from 'components/Text'
import Tooltip from 'components/Tooltip'
import Spinner from 'components/Spinner'

import { debounce } from 'utils/debounce'
import { wholeNumbersRegex } from 'utils/regex'

import { PageButton, PageInput, PageNumber, PageSize, Pagination as Container } from './styles'
import { TableVariant, TableVariantType } from '.'
import { isInlineTable } from './helpers'

const getPageSizeOptions = (t: TFunction): DropdownItem<number>[] => [
  {
    value: 5,
    label: t('row_count', { count: 5 }),
  },
  {
    value: 10,
    label: t('row_count', { count: 10 }),
  },
  {
    value: 20,
    label: t('row_count', { count: 20 }),
  },
  {
    value: 50,
    label: t('row_count', { count: 50 }),
  },
  {
    value: 100,
    label: t('row_count', { count: 100 }),
  },
]

export enum PageNumberActions {
  GoBack = 'GO_BACK',
  GoFirst = 'GO_FIRST',
  GoLast = 'GO_LAST',
  GoNext = 'GO_NEXT',
  GoTo = 'GO_TO',
}

export type PageNumberActionType = typeof PageNumberActions[keyof typeof PageNumberActions]

type Props = {
  isLoading?: boolean
  isFetching?: boolean
  isVisible: boolean
  lastPage: number
  onChangePageNumber?: (action: PageNumberActionType, pageNumber?: number) => void
  onChangePageSize?: (pageSize: number) => void
  pageNumber: number
  pageSize: number
  rowsCount: number
  totalDataCount: number
  borderless?: boolean
  variant?: TableVariant
}

const Pagination: React.FC<Props> = ({
  isLoading,
  isFetching = false,
  isVisible,
  lastPage,
  onChangePageNumber,
  onChangePageSize,
  pageNumber: pageNumberProp,
  pageSize,
  rowsCount,
  totalDataCount,
  borderless,
  variant = TableVariantType.default,
}) => {
  const { t } = useTranslation()

  const [pageNumber, setPageNumber] = useState(pageNumberProp)
  const pageSizeOptions = useMemo(() => getPageSizeOptions(t), [t])

  const { canGoBack, canGoNext, end, start } = useMemo(() => {
    const start = (pageNumberProp - 1) * pageSize + 1

    return {
      canGoBack: pageNumberProp > 1,
      canGoNext: lastPage > pageNumberProp,
      end: start - 1 + (pageSize > rowsCount ? rowsCount : pageSize),
      start,
    }
  }, [lastPage, pageNumberProp, pageSize, rowsCount])

  const handleChangePageNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!wholeNumbersRegex.test(e.target.value)) {
      return
    }

    const newPageNumber = Number(e.target.value)

    setPageNumber(newPageNumber)

    debounce(() => {
      if (newPageNumber >= 1 && newPageNumber <= lastPage && newPageNumber !== pageNumberProp) {
        onChangePageNumber?.(PageNumberActions.GoTo, newPageNumber)
      }
    }, 350)
  }

  const handleChangePageSize = (pageSize: DropdownItem<number>) => {
    onChangePageSize?.(pageSize.value)
  }

  const handleFocusPageNumber = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
  }

  const handleBlurPageNumber = () => {
    if (Number(pageNumber) < 1 || Number(pageNumber) > lastPage) {
      setPageNumber(pageNumberProp)
    }
  }

  useEffect(() => {
    let abort = false

    if (!abort) {
      setPageNumber(pageNumberProp)
    }

    return () => {
      abort = true
    }
  }, [pageNumberProp])

  if (!isVisible) {
    return null
  }

  return (
    <Container variant={variant} borderless={borderless}>
      <PageSize>
        <Text color={isInlineTable(variant) ? 'gray5' : 'gray7'} paddingRight={3}>
          show
        </Text>
        <Dropdown
          name="pageSize"
          onChange={handleChangePageSize}
          value={pageSizeOptions.find((i) => i.value === pageSize)}
          options={pageSizeOptions}
          selectValueContainerColor={isInlineTable(variant) ? 'gray2' : 'transparent'}
          width={145}
          usePortal
        />
        <Box marginLeft="1rem">
          <Spinner isVisible={isFetching} />
        </Box>
      </PageSize>
      <PageNumber>
        {isLoading ? (
          <Skeleton width="100px" />
        ) : (
          <Text>{t('pagination_range', { end, start, total: totalDataCount })}</Text>
        )}
        <Flex gap="0">
          <Tooltip title="pagination_go_first_page">
            <PageButton
              onClick={() => onChangePageNumber?.(PageNumberActions.GoFirst)}
              disabled={!canGoBack}
              width={32}
              noBorderRight
            >
              <Icon icon="chevron-double-left" />
            </PageButton>
          </Tooltip>
          <PageButton disabled={!canGoBack} onClick={() => onChangePageNumber?.(PageNumberActions.GoBack)} width={32}>
            <Icon icon="chevron-left" />
          </PageButton>
        </Flex>
        {isLoading ? (
          <Skeleton width="120px" />
        ) : (
          <Flex gap="0.5rem">
            <Text color="disabledText">page</Text>
            <Box width={42} height={32}>
              <PageInput
                onChange={handleChangePageNumber}
                onFocus={handleFocusPageNumber}
                onBlur={handleBlurPageNumber}
                value={pageNumber}
                variant="boxless"
                height="32px"
                placeholder=""
              />
            </Box>
            <Text color="disabledText" fontSize={14} fontWeight="medium">
              {t('of_count', { count: lastPage })}
            </Text>
          </Flex>
        )}
        <Flex gap="0">
          <PageButton disabled={!canGoNext} onClick={() => onChangePageNumber?.(PageNumberActions.GoNext)} width={32}>
            <Icon icon="chevron-right" />
          </PageButton>
          <Tooltip title="pagination_go_last_page" position="topRight">
            <PageButton
              onClick={() => onChangePageNumber?.(PageNumberActions.GoLast)}
              disabled={!canGoNext}
              width={32}
              noBorderLeft
            >
              <Icon icon="chevron-double-right" />
            </PageButton>
          </Tooltip>
        </Flex>
      </PageNumber>
    </Container>
  )
}

export default Pagination
